import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import HeaderV2 from '../components/HeaderV2';
import FooterV2 from '../components/FooterV2';
import FunctionalHeroV2 from '../components/FunctionalHeroV2';
import MemberPage from './MemberPage';

/**
 * MemberV2Page
 * 做法 A：外殼換 V2，內容 100% 沿用 MemberPage（含 8 個 tabs）。
 * 透過 CSS overlay 將原本的灰底卡片視覺包進「奶油色 Barista Map」底層，
 * 並加上共用 FunctionalHeroV2 壓縮深色 Hero。
 *
 * 對應路由（已在 App.tsx 註冊）：/zh/member/v2/*、/en/member/v2/*
 */
export default function MemberV2Page() {
  const location = useLocation();
  const isEn = location.pathname.startsWith('/en');
  const lang = isEn ? 'en' : 'zh';
  const isPreviewRoute = location.pathname.includes('/v2');
  const canonicalUrl = `https://www.ongucoffee.com/${lang}/member`;

  // 從 pathname 推算當前 tab，用於 Hero 副標
  const pathParts = location.pathname.split('/');
  const memberIdx = pathParts.indexOf('member');
  // /zh/member/v2/orders → tab 在 v2 後第一段
  const currentTab =
    pathParts[memberIdx + 1] === 'v2' ? pathParts[memberIdx + 2] || 'overview' : 'overview';

  // 注入 Barista Map 字型
  useEffect(() => {
    const fontId = 'v2-fonts-member';
    if (!document.getElementById(fontId)) {
      const link = document.createElement('link');
      link.id = fontId;
      link.rel = 'stylesheet';
      link.href =
        'https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;0,500;1,400&family=Noto+Serif+TC:wght@300;400;500&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  // Hero 文案：依 tab 顯示不同副標
  const tabHero: Record<string, { eyebrow: { zh: string; en: string }; title: { zh: string; en: string } }> = {
    overview: {
      eyebrow: { zh: '會員中心', en: 'Member' },
      title: { zh: '帳戶總覽', en: 'Overview' },
    },
    orders: {
      eyebrow: { zh: '會員中心', en: 'Member' },
      title: { zh: '歷史訂單', en: 'Order History' },
    },
    subscriptions: {
      eyebrow: { zh: '會員中心', en: 'Member' },
      title: { zh: '訂閱進度', en: 'Subscriptions' },
    },
    points: {
      eyebrow: { zh: '會員中心', en: 'Member' },
      title: { zh: '點數紀錄', en: 'Points' },
    },
    wishlist: {
      eyebrow: { zh: '會員中心', en: 'Member' },
      title: { zh: '收藏清單', en: 'Wishlist' },
    },
    addresses: {
      eyebrow: { zh: '會員中心', en: 'Member' },
      title: { zh: '常用地址', en: 'Addresses' },
    },
    settings: {
      eyebrow: { zh: '會員中心', en: 'Member' },
      title: { zh: '帳戶設定', en: 'Settings' },
    },
  };

  const heroData = tabHero[currentTab] || tabHero.overview;
  const breadcrumbs = [
    { label: isEn ? 'Home' : '首頁', href: `/${lang}` },
    { label: isEn ? 'Member' : '會員中心', href: `/${lang}/member/v2/overview` },
    { label: isEn ? heroData.title.en : heroData.title.zh },
  ];

  return (
    <>
      <Helmet>
        {isPreviewRoute && <meta name="robots" content="noindex,follow" />}
        {isPreviewRoute && <link rel="canonical" href={canonicalUrl} />}
        <title>
          {isEn
            ? `${heroData.title.en} | OnGU Studio Member`
            : `${heroData.title.zh}｜OnGU Studio 會員中心`}
        </title>
      </Helmet>

      <div
        className="min-h-screen bg-[#f5f1ea]"
        style={{ fontFamily: '"Noto Serif TC", "EB Garamond", serif' }}
      >
        <HeaderV2 />

        <FunctionalHeroV2
          eyebrow={isEn ? heroData.eyebrow.en : heroData.eyebrow.zh}
          title={isEn ? heroData.title.en : heroData.title.zh}
          backgroundImage="https://images.pexels.com/photos/2074130/pexels-photo-2074130.jpeg?auto=compress&cs=tinysrgb&w=1920"
          breadcrumbs={breadcrumbs}
        />

        {/* 奶油色功能區 — 包裹原 MemberPage 內容 */}
        <section className="bg-[#f5f1ea] py-12 lg:py-20">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-[50px]">
            {/* 用 wrapper 把 MemberPage 內部的 pt-24 / bg-gray-50 視覺中和 */}
            <div className="member-v2-wrapper">
              <MemberPage />
            </div>
          </div>
        </section>

        <FooterV2 />

        {/* MemberPage 內部寫死了 pt-24 / min-h-screen / bg-gray-50。
            用 scoped CSS 將其視覺融入奶油色 Barista Map 風格，
            同時保留所有功能（tabs、訂單分頁、訂閱進度、地址、設定）。 */}
        <style>{`
          .member-v2-wrapper > div {
            min-height: auto !important;
            padding-top: 0 !important;
            padding-bottom: 0 !important;
            background: transparent !important;
          }
          .member-v2-wrapper > div > div {
            padding-left: 0 !important;
            padding-right: 0 !important;
            max-width: 100% !important;
          }
          /* 將內部白底卡片 → 奶油色襯托上的純白卡片，留出邊距、加細邊框 */
          .member-v2-wrapper .bg-white,
          .member-v2-wrapper .dark\\:bg-gray-950 {
            background-color: #ffffff !important;
          }
          /* 內部 sidebar 卡片 active 狀態保留，但外觀更乾淨 */
          .member-v2-wrapper aside,
          .member-v2-wrapper .lg\\:w-80 {
            font-family: "Noto Serif TC", "EB Garamond", serif;
          }
          /* 內部 H2 大標 → 改用襯線 */
          .member-v2-wrapper h2 {
            font-family: "EB Garamond", "Noto Serif TC", serif !important;
            font-weight: 300 !important;
            letter-spacing: 0.02em;
          }
        `}</style>
      </div>
    </>
  );
}
